import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const MarketingTools = () => {
  const [selectedCategory, setSelectedCategory] = useState('promotional');

  const categories = [
    { value: 'promotional', label: 'Promotional Materials' },
    { value: 'digital', label: 'Digital Assets' },
    { value: 'training', label: 'Training Resources' },
    { value: 'support', label: 'Sales Support' }
  ];

  const marketingMaterials = {
    promotional: [
      {
        title: 'Holiday Transfer Promotion Flyer',
        description: 'Promote 25% off transfer fees for the holiday season',
        type: 'PDF',
        size: '2.4 MB',
        downloads: 1247,
        category: 'Print Materials',
        icon: 'FileText',
        tags: ['Holiday', 'Discount', 'Print Ready']
      },
      {
        title: 'Agent Business Cards Template',
        description: 'Customizable business cards with your agent information',
        type: 'PSD',
        size: '15.2 MB',
        downloads: 892,
        category: 'Business Materials',
        icon: 'CreditCard',
        tags: ['Business Cards', 'Template', 'Customizable']
      },
      {
        title: 'Rate Comparison Sheet',
        description: 'Show customers how our rates beat competitors',
        type: 'PDF',
        size: '1.8 MB',
        downloads: 2156,
        category: 'Sales Tools',
        icon: 'BarChart3',
        tags: ['Rates', 'Comparison', 'Sales']
      },
      {
        title: 'Customer Testimonials Booklet',
        description: 'Real customer stories and testimonials',
        type: 'PDF',
        size: '4.1 MB',
        downloads: 687,
        category: 'Trust Building',
        icon: 'MessageSquare',
        tags: ['Testimonials', 'Trust', 'Stories']
      }
    ],
    digital: [
      {
        title: 'Social Media Post Templates',
        description: 'Ready-to-use social media graphics and posts',
        type: 'ZIP',
        size: '45.7 MB',
        downloads: 1534,
        category: 'Social Media',
        icon: 'Share2',
        tags: ['Social Media', 'Graphics', 'Templates']
      },
      {
        title: 'Email Marketing Templates',
        description: 'Professional email templates for customer outreach',
        type: 'HTML',
        size: '8.3 MB',
        downloads: 923,
        category: 'Email Marketing',
        icon: 'Mail',
        tags: ['Email', 'Templates', 'Marketing']
      },
      {
        title: 'Website Banner Set',
        description: 'Web banners in various sizes for online promotion',
        type: 'ZIP',
        size: '12.4 MB',
        downloads: 756,
        category: 'Web Assets',
        icon: 'Monitor',
        tags: ['Banners', 'Web', 'Online']
      }
    ],
    training: [
      {
        title: 'Agent Onboarding Video Series',
        description: 'Complete training videos for new agents',
        type: 'Video',
        size: '2.1 GB',
        downloads: 445,
        category: 'Training Videos',
        icon: 'Play',
        tags: ['Training', 'Video', 'Onboarding']
      },
      {
        title: 'Compliance Guidelines Manual',
        description: 'Essential compliance and regulatory information',
        type: 'PDF',
        size: '6.8 MB',
        downloads: 1823,
        category: 'Compliance',
        icon: 'Shield',
        tags: ['Compliance', 'Manual', 'Regulations']
      },
      {
        title: 'Sales Technique Handbook',
        description: 'Proven sales strategies for money transfer services',
        type: 'PDF',
        size: '3.2 MB',
        downloads: 1156,
        category: 'Sales Training',
        icon: 'TrendingUp',
        tags: ['Sales', 'Techniques', 'Training']
      }
    ],
    support: [
      {
        title: 'Customer FAQ Sheet',
        description: 'Common questions and answers for quick reference',
        type: 'PDF',
        size: '1.1 MB',
        downloads: 2387,
        category: 'Support Materials',
        icon: 'HelpCircle',
        tags: ['FAQ', 'Support', 'Reference']
      },
      {
        title: 'Product Feature Comparison',
        description: 'Detailed comparison of service features and benefits',
        type: 'PDF',
        size: '2.7 MB',
        downloads: 987,
        category: 'Sales Support',
        icon: 'Grid3X3',
        tags: ['Features', 'Comparison', 'Benefits']
      },
      {
        title: 'Objection Handling Guide',
        description: 'How to address common customer concerns and objections',
        type: 'PDF',
        size: '1.9 MB',
        downloads: 1432,
        category: 'Sales Support',
        icon: 'MessageCircle',
        tags: ['Objections', 'Sales', 'Guide']
      }
    ]
  };

  const currentMaterials = marketingMaterials?.[selectedCategory] || [];

  const handleDownload = (material) => {
    console.log('Downloading:', material?.title);
    // Simulate download action
  };

  const getFileTypeColor = (type) => {
    const colors = {
      PDF: 'text-error',
      PSD: 'text-primary',
      ZIP: 'text-warning',
      HTML: 'text-success',
      Video: 'text-purple-600'
    };
    return colors?.[type] || 'text-muted-foreground';
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Marketing Tools & Resources</h3>
            <p className="text-sm text-muted-foreground">
              Download promotional materials, training resources, and sales support tools
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            iconName="ExternalLink"
            iconPosition="right"
            onClick={() => console.log('Open marketing portal')}
          >
            Marketing Portal
          </Button>
        </div>

        {/* Category Filter */}
        <div className="flex space-x-1 bg-muted/50 p-1 rounded-lg">
          {categories?.map(category => (
            <button
              key={category?.value}
              onClick={() => setSelectedCategory(category?.value)}
              className={`flex-1 px-3 py-2 text-sm font-medium rounded transition-colors duration-200 ${
                selectedCategory === category?.value
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {category?.label}
            </button>
          ))}
        </div>

        {/* Materials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {currentMaterials?.map((material, index) => (
            <div
              key={index}
              className="border border-border rounded-lg p-4 hover:bg-muted/20 transition-colors duration-200"
            >
              <div className="flex items-start space-x-4">
                {/* Icon */}
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name={material?.icon} size={24} className="text-primary" />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold text-foreground">{material?.title}</h4>
                    <span className={`text-xs font-medium ${getFileTypeColor(material?.type)}`}>
                      {material?.type}
                    </span>
                  </div>
                  
                  <p className="text-sm text-muted-foreground mb-3">{material?.description}</p>
                  
                  {/* Tags */}
                  <div className="flex flex-wrap gap-1 mb-3">
                    {material?.tags?.map((tag, tagIndex) => (
                      <span
                        key={tagIndex}
                        className="px-2 py-1 text-xs bg-muted text-muted-foreground rounded"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  
                  {/* Metadata */}
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
                    <span>{material?.category}</span>
                    <span>{material?.size}</span>
                    <div className="flex items-center space-x-1">
                      <Icon name="Download" size={12} />
                      <span>{material?.downloads?.toLocaleString()} downloads</span>
                    </div>
                  </div>
                  
                  {/* Action */}
                  <Button
                    variant="outline"
                    size="sm"
                    fullWidth
                    iconName="Download"
                    iconPosition="left"
                    onClick={() => handleDownload(material)}
                  >
                    Download
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Marketing Tips */}
        <div className="bg-gradient-to-r from-success/5 to-success/10 border border-success/20 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Icon name="Lightbulb" size={20} className="text-success mt-0.5" />
            <div>
              <h4 className="font-semibold text-success mb-2">Marketing Tips for Agents</h4>
              <div className="space-y-2 text-sm text-success/80">
                <div className="flex items-start space-x-2">
                  <Icon name="Check" size={12} className="mt-1" />
                  <span>Customize materials with your contact information before distribution</span>
                </div>
                <div className="flex items-start space-x-2">
                  <Icon name="Check" size={12} className="mt-1" />
                  <span>Focus on community events and local business partnerships</span>
                </div>
                <div className="flex items-start space-x-2">
                  <Icon name="Check" size={12} className="mt-1" />
                  <span>Track which materials generate the most customer interest</span>
                </div>
                <div className="flex items-start space-x-2">
                  <Icon name="Check" size={12} className="mt-1" />
                  <span>Use social media templates to maintain consistent branding</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Request Custom Materials */}
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-semibold text-foreground mb-1">Need Custom Materials?</h4>
              <p className="text-sm text-muted-foreground">
                Request custom marketing materials tailored to your specific market or campaign needs.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              iconName="Plus"
              iconPosition="left"
              onClick={() => console.log('Request custom materials')}
            >
              Request Custom
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketingTools;